import { zodResolver } from "@hookform/resolvers/zod";
import type { LongRadius90DefectDetailsData } from "@stryker/core/types/sections";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TextInput } from "@/components/ui/textInput";
import { Typography } from "@/components/ui/typography";
import { inputStep } from "@/constants/inputs";
import { useConfigOptions } from "@/hooks/useConfigOptions";
import useCUMechanicalJobStore from "@/store/useCUMechanicalJobStore";

const numericField = z.union([
  z.number().lt(2147483647),
  z.string().refine((value) => !Number.isNaN(Number.parseFloat(value)), { message: "Must be a number" }),
]);

const longRadius90DefectDetailsSchema = z.object({
  weld_thickness_1: numericField,
  unit_for_weld_thickness_1: z.string(),
  passed_weld_1: numericField,
  unit_for_passed_weld_1: z.string(),
  weld_thickness_2: numericField,
  unit_for_weld_thickness_2: z.string(),
  passed_weld_2: numericField,
  unit_for_passed_weld_2: z.string(),
});

type LongRadius90DefectDetailsSchema = z.infer<typeof longRadius90DefectDetailsSchema>;

interface LongRadius90DefectDetailsProps {
  onSave: (data: LongRadius90DefectDetailsData) => void;
}

export function LongRadius90DefectDetails({ onSave }: LongRadius90DefectDetailsProps) {
  const { sections } = useCUMechanicalJobStore();
  const savedDefectDetails = sections.defectDetails;
  const { measureUnitOptions } = useConfigOptions();
  const data = savedDefectDetails.data as LongRadius90DefectDetailsData;

  const form = useForm<LongRadius90DefectDetailsSchema>({
    resolver: zodResolver(longRadius90DefectDetailsSchema),
    defaultValues: {
      weld_thickness_1: data.weld_thickness_1 ?? "",
      unit_for_weld_thickness_1: data.unit_for_weld_thickness_1 ?? "Inches",
      passed_weld_1: data.passed_weld_1 ?? "",
      unit_for_passed_weld_1: data.unit_for_passed_weld_1 ?? "Inches",
      weld_thickness_2: data.weld_thickness_2 ?? "",
      unit_for_weld_thickness_2: data.unit_for_weld_thickness_2 ?? "Inches",
      passed_weld_2: data.passed_weld_2 ?? "",
      unit_for_passed_weld_2: data.unit_for_passed_weld_2 ?? "Inches",
    },
  });

  const handleSave = (payload: LongRadius90DefectDetailsSchema) => {
    const result: LongRadius90DefectDetailsData = {
      ...payload,
      repair_length_options: data.repair_length_options,
      repair_length_repair_kit_sizes: data.repair_length_repair_kit_sizes,
    };
    onSave(result);
  };

  return (
    <div className="space-y-6">
      <Typography variant="text-xl">Long Radius 90 Defect Details</Typography>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSave)} className="flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <Typography variant="text-lg">Top</Typography>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="weld_thickness_1"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Weld Thickness 1</FormLabel>
                    <FormControl>
                      <TextInput type="number" step={inputStep} placeholder="Enter weld thickness 1" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="unit_for_weld_thickness_1"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Unit</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select unit" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {measureUnitOptions.map((unit, index) => (
                          <SelectItem key={`longradius-unit-${unit.value}-${index}`} value={unit.value}>
                            {unit.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="passed_weld_1"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Passed Weld 1</FormLabel>
                    <FormControl>
                      <TextInput type="number" step={inputStep} placeholder="Enter passed weld 1" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="unit_for_passed_weld_1"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Unit</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select unit" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {measureUnitOptions.map((unit, index) => (
                          <SelectItem key={`longradius-unit-${unit.value}-${index}`} value={unit.value}>
                            {unit.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className="border-gray-200 border-t" />

          <div className="flex flex-col gap-4">
            <Typography variant="text-lg">Bottom</Typography>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="weld_thickness_2"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Weld Thickness 2</FormLabel>
                    <FormControl>
                      <TextInput type="number" step={inputStep} placeholder="Enter weld thickness 2" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="unit_for_weld_thickness_2"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Unit</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select unit" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {measureUnitOptions.map((unit, index) => (
                          <SelectItem key={`longradius-unit-${unit.value}-${index}`} value={unit.value}>
                            {unit.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="passed_weld_2"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Passed Weld 2</FormLabel>
                    <FormControl>
                      <TextInput type="number" step={inputStep} placeholder="Enter passed weld 2" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="unit_for_passed_weld_2"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Unit</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select unit" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {measureUnitOptions.map((unit, index) => (
                          <SelectItem key={`longradius-unit-${unit.value}-${index}`} value={unit.value}>
                            {unit.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <Button type="submit" className="ml-auto">
            Save
          </Button>
        </form>
      </Form>
    </div>
  );
}
